import {
  QueryClient,
  QueryFunctionContext,
  useInfiniteQuery,
} from "@tanstack/react-query";

const VOCABULARY_LIST_QUERY_KEY = "vocabulary-list-query";

export function invalidateQuery(client: QueryClient): void {
  client.invalidateQueries({ queryKey: [VOCABULARY_LIST_QUERY_KEY] });
}

export interface VocabularyDto {
  id: number;
  word: string;
  word_language: string;
  pronunciation_status?: "NOT_REQUESTED" | "PENDING" | "COMPLETE" | "FAILED";
  pronunciation_audio?: string | null;
  translation_language?: string;
  translation: string;
  translation_details?: {
    translation_word?: string;
    source_surface_form?: string | null;
    source_dictionary_form?: string | null;
    source_lemma?: string | null;
    source_article?: string | null;
    source_plural_form?: string | null;
    source_context_hint?: string | null;
    grammar?: {
      part_of_speech?: string | null;
      tense?: string | null;
      mood?: string | null;
      person?: string | null;
      number?: string | null;
      gender?: string | null;
      case?: string | null;
      syntactic_function?: string | null;
    } | null;
    alternative_meanings?: string | null;
    translated_sentence?: string | null;
  };
  winning_streak: number;
  last_tested: string | null;
  review_direction?: "WORD_TO_TRANSLATION" | "TRANSLATION_TO_WORD";
  mnemonic: string | null;
  sentence: string | null;
  status: "PENDING" | "COMPLETE" | "FAILED";
  origin?: "MANUAL" | "PHOTO_IMPORT" | string;
  photo_import_id?: number | null;
  usages: {
    text: number;
    chapter_id: number | null;
    chapter_counter?: number | null;
    start_idx: number;
    end_idx: number;
  }[];
}

interface VocabularyDtoPage {
  next: string;
  results: VocabularyDto[];
}

const fetchPage = async (
  pageParam: string,
  generated_text_id?: number,
  generated_text_created_at_last_days?: number,
  languages?: string[],
  origins?: string[],
  signal?: AbortSignal,
) => {
  const queryParams = new URLSearchParams({
    cursor: pageParam,
  });
  if (languages !== undefined) {
    for (const lang of languages) {
      queryParams.append("language", lang);
    }
  }
  if (generated_text_id !== undefined) {
    queryParams.append("generated_text_id", generated_text_id.toString());
  }
  if (generated_text_created_at_last_days !== undefined) {
    queryParams.append(
      "generated_text_created_at_last_days",
      generated_text_created_at_last_days.toString(),
    );
  }
  if (origins !== undefined) {
    for (const origin of origins) {
      queryParams.append("origin", origin);
    }
  }
  const res = await fetch(
    "/api/reeprise/vocabulary/?" + queryParams.toString(),
    {
      signal,
    },
  );

  if (!res.ok) {
    throw new Error("could not fetch vocabulary");
  }
  return (await res.json()) as VocabularyDtoPage;
};

export function usePaginatedVocabularyList(params?: {
  generated_text_id?: number;
  word_languages?: string[];
  generated_text_created_at_last_days?: number;
  origins?: string[];
}) {
  const paginatedVocabularyListQuery = useInfiniteQuery({
    queryKey: [
      VOCABULARY_LIST_QUERY_KEY,
      {
        textId: params?.generated_text_id,
        languages: params?.word_languages,
        origins: params?.origins,
        textCreatedWindow: params?.generated_text_created_at_last_days,
      },
    ],
    queryFn: ({ pageParam, signal }: QueryFunctionContext) =>
      fetchPage(
        pageParam as string,
        params?.generated_text_id,
        params?.generated_text_created_at_last_days,
        params?.word_languages,
        params?.origins,
        signal,
      ),
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.next,
    placeholderData: (previousData) => previousData,
    refetchInterval: (query) => {
      if (
        query.state.data &&
        query.state.data.pages
          .flatMap((page) => page.results)
          .find(
            (voc) =>
              voc.status === "PENDING" || voc.pronunciation_status === "PENDING",
          )
      ) {
        return 500;
      }
      return false;
    },
  });

  return paginatedVocabularyListQuery;
}
